// Shared per-bill display-field derivation, extracted from Legislation.js's render loop so
// the Legislation list, report previews, the public report page, and PDF/Word exports all
// derive identical display text (bill number, author, location, hearing info) from one place.

export function deriveBillDisplayFields(bill) {
  let billDisplay;
  if (bill.session_number > 0) {
    billDisplay = `${bill.bill_type}x${bill.session_number} ${bill.bill_number}`;
  } else {
    billDisplay = `${bill.bill_type} ${bill.bill_number}`;
  }

  const latestVersion = bill.LatestVersion || (bill.BillVersions && bill.BillVersions.length > 0
    ? bill.BillVersions.reduce((prev, curr) => (prev.bill_version < curr.bill_version ? prev : curr))
    : null);

  let authorName = 'N/A';
  const leadAuthorRecord = latestVersion?.BillVersionAuthors?.find(a => a.contribution === 'Lead Author');
  if (leadAuthorRecord) {
    authorName = leadAuthorRecord.author_name || leadAuthorRecord.committee_members || 'N/A';
  } else if (latestVersion?.Legislator) {
    authorName = latestVersion.Legislator.legislator_name;
  } else if (latestVersion?.BillVersionAuthors?.length) {
    const primaryAuthor = latestVersion.BillVersionAuthors.find(a => a.primary_author_flag === 'Y') || latestVersion.BillVersionAuthors[0];
    authorName = primaryAuthor.author_name || primaryAuthor.committee_members || 'N/A';
  }
  if (authorName === 'N/A' && bill.Author) {
    authorName = `${bill.Author.first_name} ${bill.Author.last_name}`.trim();
  }

  // Bills awaiting the Governor's action are the one state where `status`/`location` are
  // unreliable: depending on house and data-source quirks they show up as status 'Enrolled'
  // (location null), status 'Passed' + location 'Enrollment', or even status 'Passed' with
  // location still null. `last_action_description` usually tracks the bill's true current
  // stage, but it doesn't reliably flip away from 'Enrolled' once the Governor acts — so once
  // `status` itself has reached a resolved terminal value (e.g. Vetoed, Chaptered), that's the
  // ground truth and wins over a stale 'Enrolled' `last_action_description`.
  const RESOLVED_STATUSES = ['Vetoed', 'Chaptered', 'Signed by Governor', 'Died', 'Failed', 'Failed Passage in Committee'];
  const isEnrolled = bill.last_action_description === 'Enrolled' && !RESOLVED_STATUSES.includes(bill.status);
  // 'Awaiting Governor' rides in `location` (not `status`) so it renders through the same
  // bold-status/plain-location pattern every consumer already uses for every other status.
  const location = isEnrolled ? 'Awaiting Governor' : (bill.LocationCode ? bill.LocationCode.description : (bill.DailyFile?.LocationCode?.description || ''));
  const status = isEnrolled ? 'Enrolled' : (bill.status || '');

  let fileLocationDisplay = null;
  if (!isEnrolled && status !== 'Chaptered' && bill.DailyFile && bill.DailyFile.file_location) {
    const rawLoc = bill.DailyFile.file_location;
    const fileNum = parseInt(rawLoc.replace(/^A-\s*/i, ''), 10);
    fileLocationDisplay = (location && location.includes('Inactive'))
      ? `File #A- ${fileNum}`
      : `File #${fileNum}`;
  }

  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });

  const upcomingHearing = bill.CommitteeHearings?.find(hearing =>
    hearing.committee_code === bill.location &&
    (hearing.hearing_date || '').split('T')[0] >= todayStr
  );

  let hearingInfo = null;
  if (upcomingHearing) {
    const hearingDate = new Date(upcomingHearing.hearing_date);
    const formattedDate = hearingDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC'
    });
    const agendaTime = upcomingHearing.CommitteeAgenda?.agenda_time;
    hearingInfo = `${formattedDate}${agendaTime ? ` at ${agendaTime}` : ''}`;
  }

  return { billDisplay, latestVersion, authorName, location, status, fileLocationDisplay, hearingInfo, isEnrolled };
}
